import { useEffect } from 'react';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import useCurrentUser from 'src/api/auth/useCurrentUser';
import CurrentUser from 'src/types/CurrentUser';
import { setImitationState } from 'src/api/imitationState';
import { authzClient } from '@/context/authz/AuthzClient';

interface IUseAuth {
  isFetchingAuthInitialStatus: boolean;
  currentUser?: CurrentUser;
  error?: Error;
  isRefetching: boolean;
  refetchUser: (options?: RefetchOptions) => Promise<QueryObserverResult>;
}

function useAuth(): IUseAuth {
  const {
    data: currentUser,
    error: currentUserError,
    refetch: refetchUser,
    isLoading: isLoadingUser,
    isRefetching: isRefetchingCurrentUser,
    isFetching: isFetchingCurrentUser,
  } = useCurrentUser();

  const user = currentUser?.user;

  // Seeded during derivation, deliberately NOT in an effect: feature flags and
  // the client-side write blocks read this singleton while the very first
  // render after `/auth/me` resolves is still in flight, which is before any
  // effect would have run. Writing the same value on every render is a no-op.
  setImitationState(user?.imitation ?? null);

  useEffect(() => {
    if (user?.abilityRules) {
      // No-op when these rules are already seeded, so mounting another
      // `useAuth` caller does not re-seed and fan out cache events.
      authzClient.seedAbilityRules(user.abilityRules);
    }
  }, [user?.abilityRules]);

  return {
    isFetchingAuthInitialStatus: isLoadingUser && isFetchingCurrentUser,
    currentUser: user || undefined,
    error: currentUserError ? new Error(currentUserError.message) : undefined,
    isRefetching: isRefetchingCurrentUser,
    refetchUser,
  };
}
export default useAuth;
